// Where to send an admin after a successful Microsoft sign-in.
//
// Password login keeps this in React Router's history state: validateAdmin
// bounces an unauthenticated admin to /tg-admin with `state.from` set to where
// they were headed. Microsoft sign-in cannot use that, because it leaves the SPA
// entirely (window.location.href) and the Entra round trip returns as a fresh
// top-level navigation, not a popstate, so the history entry carrying that state
// is gone by the time the callback runs.
//
// sessionStorage survives that trip: it is scoped per origin and per tab, and the
// detour through login.microsoftonline.com happens in the same tab. Stash the
// target before leaving, take it back on the way in.
//
// Every access is guarded because sessionStorage throws outright in some privacy
// modes; losing the redirect target is not worth breaking a login over.

const KEY = 'tgAdminPostSignInPath';

// Only ever remember an in-app admin path. This also rejects a protocol-relative
// value like "//example.com", which would otherwise navigate off-site.
const isAdminPath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/tg-admin');

// Always writes or clears, never leaves the previous value in place. A sign-in
// that fails or is abandoned never reaches takePostSignInPath, so without the
// clear an abandoned target would survive in the tab and hijack the next
// successful sign-in that had no target of its own.
export const stashPostSignInPath = (path: unknown): void => {
  try {
    if (isAdminPath(path)) {
      window.sessionStorage.setItem(KEY, path);
    } else {
      window.sessionStorage.removeItem(KEY);
    }
  } catch {
    // No redirect memory available; the caller falls back to the default.
  }
};

export const takePostSignInPath = (fallback: string): string => {
  try {
    const stored = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    return isAdminPath(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};
